import { Box, Button, Stack, Typography } from '@mui/material';
import Empty from 'components/Empty';
import JSONLD from 'components/JSONLD';
import Row from 'components/Row';
import { env, HEADER_HEIGHT } from 'constants';
import { AddressContext, CartContext, CateringContext } from 'constants/contexts';
import useMerchantPath from 'hooks/useMerchantPath';
import useSentryUpdateEffect from 'hooks/useSentryUpdateEffect';
import { throttle } from 'lodash-es';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import { getTopMargin } from 'utils/app';
import { isElementInViewport } from 'utils/dom';
import { historyData } from 'utils/history';
import Categories from './Categories';
import Item from './Item';
import Menus from './Menus';
import TabsHeader from './TabsHeader';
import useMenus from './useMenus';
import useStructuredData from './useStructuredData';
import { getCategoryHeaderId } from './utils';

function MerchantItems({
  merchant,
  openScheduling,
  subheaderId,
}) {
  const [selectedMenu, setSelectedMenu] = useState();
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);
  const { itemId } = useParams();
  const { currentAddress } = useContext(AddressContext);
  const cart = useContext(CartContext);
  const catering = useContext(CateringContext);
  const { setMinimumOrderPrice } = cart;
  const history = useHistory();
  const {
    menus, onSaleList, openingHours, loading,
  } = useMenus();
  const categories = selectedMenu?.categories ?? [];
  const structuredData = useStructuredData({ merchant, menus });
  const itemsAvailable = !merchant.is_disabled &&
    (merchant?.has_scheduling_order || (merchant.takeout && selectedMenu?.isServing));
  const merchantPath = useMerchantPath(merchant);

  useEffect(() => {
    if (!selectedMenu) {
      return;
    }
    const updateActiveCategory = throttle(() => {
      const visibleIndex = selectedMenu.categories.findLastIndex((_,
        index) => isElementInViewport(document.getElementById(getCategoryHeaderId(index))));
      if (visibleIndex !== -1) {
        setActiveCategoryIndex(visibleIndex);
      }
    }, 500);

    document.addEventListener('scroll', updateActiveCategory);

    return () => document.removeEventListener('scroll', updateActiveCategory);
  }, [selectedMenu]);

  useSentryUpdateEffect(
    {
      merchantId: merchant?.id,
      selectedMenuId: selectedMenu?.id,
    },
    [selectedMenu, merchant],
  );

  useEffect(() => {
    setMinimumOrderPrice(
      selectedMenu?.external_menu_details?.minimum_order_price ||
      merchant?.parent?.minimum_order_price,
    );
  }, [selectedMenu, merchant, setMinimumOrderPrice]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, [merchant.id]);

  function handleCategoryTabClick(index) {
    setActiveCategoryIndex(index);
    window.scrollTo({
      top: ((document.getElementById(getCategoryHeaderId(index))
        ?.getBoundingClientRect()?.top ?? 0) + window.scrollY) - (HEADER_HEIGHT + getTopMargin() + 60),
      behavior: 'smooth',
    });
  }

  return (
    <Stack>
      <JSONLD data={structuredData}/>
      {menus && (
        <Menus
          options={menus}
          menu={selectedMenu}
          onChange={setSelectedMenu}
        />
      )}
      {
        categories.length > 1 && (
          <TabsHeader
            subheaderId={subheaderId}
            menu={selectedMenu}
            activeCategoryIndex={activeCategoryIndex}
            onClick={handleCategoryTabClick}
          />
        )
      }

      <Categories
        categories={[...onSaleList, ...categories]}
        merchantId={merchant.id}
        loading={
          !merchant ||
          !menus ||
          (menus.length > 0 && !selectedMenu) ||
          loading ||
          (env.IS_CATERING && !cart.scheduledTime && !selectedMenu)
        }
        disabled={!itemsAvailable}
        empty={(
          <Empty
            pt={0}
            description={(
              <Stack spacing={2}>
                <Typography gutterBottom align="center">
                  No food available at this time.
                </Typography>
                <Typography gutterBottom align="center">
                  Please select a different time.
                </Typography>
                <Button
                  size="large"
                  variant="outlined"
                  onClick={env.IS_CATERING ?
                    catering.openScheduling :
                    openScheduling}
                >
                  Edit date and time
                </Button>

                {
                  openingHours && (
                    <Stack minWidth={250} spacing={2}>
                      <Typography variant="h6" fontWeight={600} align="center">
                        Also, please check our opening hours:
                      </Typography>
                      {
                        Object.entries(openingHours).map(([day, hours]) => (
                          <Row key={day}>
                            <Typography>{day}</Typography>
                            <Box>
                              {
                                hours.map((h) => (
                                  <Typography key={h.from_hour}>
                                    {h.from_hour}
                                    {' '}
                                    -
                                    {' '}
                                    {h.to_hour}
                                  </Typography>
                                ))
                              }
                            </Box>
                          </Row>
                        ))
                      }
                    </Stack>
                  )
                }
              </Stack>
            )}
          />
        )}
      />

      <Item
        itemId={itemId}
        open={
          (env.IS_CATERING || currentAddress) &&
          itemId != null &&
          (!cart.merchant || merchant?.id === cart.merchant?.id)
        }
        showSpecialNote={merchant?.config?.show_item_special_notes}
        available={itemsAvailable}
        onBack={() => {
          if (historyData.get()?.pathStack?.length > 0) {
            history.goBack();
            return;
          }
          history.replace(merchantPath);
        }}
      />

    </Stack>
  );
}

export default MerchantItems;
